import type { EsEnLocale } from "@/i18n/config";
import type { LocalizedText } from "@/components/RegionalLayout/types";

export const TARIFA_TIPOS = ["destino", "zona_corta", "traslado"] as const;
export type TarifaTipo = (typeof TARIFA_TIPOS)[number];

/** Una tarifa tal como la devuelve GET /api/tarifas. Los montos vienen como
 *  string decimal ("30.00"), por eso todo pasa por `aNumero`. */
export interface Tarifa {
  id: number;
  codigo: string;
  tipo: TarifaTipo;
  destino: LocalizedText;
  moneda: string;
  precio_base: string;
  pasajeros_incluidos: number;
  precio_pasajero_adicional: string | null;
  /** Sólo llega si se pidió `cant_pasajero`; el cliente no lo usa. */
  precio_calculado: string | null;
  precio_tres_destinos: string | null;
  precio_dos_destinos: string | null;
  /** Horas incluidas en los traslados/tours. */
  horas_incluidas: number | null;
  precio_hora_adicional: string | null;
}

export interface AeropuertoTaxi {
  id: number;
  nombre: LocalizedText;
  descripcion: LocalizedText;
  iata: string;
  icao: string;
  latitud: string;
  longitud: string;
}

export type TipoVehiculo = "sedan" | "suv" | "van";

/** Cuerpo de POST /api/reservas. Desde la actualización el precio ya no se
 *  envía: lo calcula el servidor y lo devuelve en la respuesta. */
export interface ReservaTaxi {
  aeropuerto_iata: string;
  tarifa_id: number;
  fecha: string;
  hora: string;
  cant_pasajero: number;
  tipo_vehiculo: TipoVehiculo;
  nombre: string;
  correo: string;
  telefono?: string;
  numero_vuelo?: string;
  terminal?: string;
  lang?: EsEnLocale;
}

/** Datos del viaje que ya conocemos antes de pedir nombre/correo/teléfono. */
export type ReservaTaxiPendiente = Omit<
  ReservaTaxi,
  "nombre" | "correo" | "telefono"
>;

export interface ReservaCreada {
  ok: boolean;
  id: number;
  estado: string;
  lang: string;
  tarifa_id: number;
  tarifa_codigo: string;
  destino: string;
  /** Precio final, calculado y confirmado por el servidor. */
  precio: string;
}

export type ReservaResponse =
  | { success: true; data: ReservaCreada }
  | { success: false; message: string; errors?: Record<string, string[]> };

function aNumero(valor: string | null): number {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Mismo cálculo que hace el servidor en `precio_calculado`: la base cubre
 * `pasajeros_incluidos` y cada pasajero extra suma `precio_pasajero_adicional`.
 * Lo resolvemos en el cliente para no pedir una tarifa nueva cada vez que el
 * usuario mueve el contador de pasajeros; el importe que vale es igual el que
 * devuelve el POST.
 */
export function calcularPrecio(tarifa: Tarifa, pasajeros: number): number {
  const adicionales = Math.max(0, pasajeros - tarifa.pasajeros_incluidos);
  return (
    aNumero(tarifa.precio_base) +
    aNumero(tarifa.precio_pasajero_adicional) * adicionales
  );
}

/** Formatea un importe sin decimales cuando es redondo ("30", "32.50"). */
export function formatearPrecio(monto: number): string {
  return Number.isInteger(monto) ? String(monto) : monto.toFixed(2);
}
